import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHead, Btn, Card, Field, Toggle, Label } from "@/admin/ui";
import { toast } from "sonner";
import { Loader2, Download, Eye } from "lucide-react";

const LaunchAdmin = () => {
  const [cfg, setCfg] = useState(null);
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/launch").then((r) => {
    const { subscribers, ...rest } = r.data;
    setCfg(rest);
    setSubs(subscribers || []);
  }).catch(() => {});

  useEffect(() => { load(); }, []);

  const up = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/launch", {
        launch_mode_enabled: !!cfg.launch_mode_enabled,
        launch_at: cfg.launch_at || "",
        launch_heading: cfg.launch_heading || "",
        launch_subheading: cfg.launch_subheading || "",
      });
      const { subscribers, ...rest } = data;
      setCfg(rest);
      setSubs(subscribers || []);
      toast.success("Launch settings saved");
    } catch (e) {
      toast.error("Could not save");
    }
    setBusy(false);
  };

  const del = async (email) => {
    try {
      const { data } = await api.post("/admin/launch/remove", { email });
      const { subscribers, ...rest } = data;
      setCfg(rest); setSubs(subscribers || []);
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  const csv = () => {
    if (!subs.length) return;
    const rows = subs.map((s) => ({ email: s.email, notified: s.notified ? "yes" : "no", at: s.at }));
    const keys = Object.keys(rows[0]);
    const body = [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([body], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "launch-early-access.csv"; a.click();
  };

  if (!cfg) return <div className="text-espresso/40">Loading…</div>;

  return (
    <div className="max-w-2xl">
      <PageHead title="Launch Mode" sub="Lock the store behind a countdown landing page. Visitors can join the early-access list and get an automatic 'we're live' email the moment you open." />

      <Card className="mb-4">
        <Toggle
          label={`Launching soon mode ${cfg.launch_mode_enabled ? "ON — store hidden from visitors" : "OFF — store is open"}`}
          value={cfg.launch_mode_enabled}
          onChange={(v) => up("launch_mode_enabled", v)}
          testid="launch-mode-toggle" />
        <p className="text-xs text-espresso/50 -mt-2 mb-5">
          While ON, everyone visiting the site sees only the coming-soon page. You (and staff) still see the full store, with a reminder banner. It opens automatically at the launch time below, and early-access subscribers are emailed once.
        </p>

        <Field label="Launch date & time (Glasgow time)" type="datetime-local"
          value={cfg.launch_at} onChange={(v) => up("launch_at", v)} testid="launch-at" />

        <Field label="Countdown subheading (shown to visitors)" textarea
          value={cfg.launch_subheading} onChange={(v) => up("launch_subheading", v)} testid="launch-subheading"
          placeholder="A heritage-led house of contemporary pieces. Something considered, and worth the wait." />

        <Field label="Launch email heading (optional)"
          value={cfg.launch_heading} onChange={(v) => up("launch_heading", v)} testid="launch-heading"
          placeholder="We're live." />

        <Btn onClick={save} disabled={busy} testid="launch-save">
          {busy ? <Loader2 size={13} className="animate-spin inline" /> : null} Save launch settings
        </Btn>
        {cfg.launch_notified && (
          <p className="text-xs text-sage mt-3">Launch email already sent for the current date. Change the date to re-arm it.</p>
        )}
      </Card>

      <Card className="mb-4">
        <Label>Preview the countdown page</Label>
        <p className="text-xs text-espresso/50 mt-1 mb-4">
          See exactly what visitors see on the coming-soon page — the countdown, waitlist and sound — without changing your real launch date. An "Exit countdown preview" button appears so you can return to the store anytime.
        </p>
        <button
          onClick={() => {
            try { localStorage.setItem("nba_preview_countdown", "true"); } catch {}
            window.open("/", "_blank");
          }}
          data-testid="preview-countdown-btn"
          className="inline-flex items-center gap-2 border border-espresso px-4 py-2 text-[0.62rem] tracking-[0.2em] uppercase hover:bg-espresso hover:text-ivory transition-colors">
          <Eye size={13} /> Preview countdown page
        </button>
      </Card>

      <Card>
        <div className="flex justify-between items-center mb-3">
          <Label>Early-access list ({subs.length})</Label>
          <button onClick={csv} disabled={!subs.length} data-testid="launch-export"
            className="flex items-center gap-1.5 text-[0.62rem] tracking-widest uppercase border border-espresso px-2.5 py-1 disabled:opacity-40">
            <Download size={12} /> Export CSV
          </button>
        </div>
        <ul className="text-sm space-y-1 text-espresso/70 max-h-72 overflow-y-auto" data-testid="launch-subs">
          {subs.map((s, i) => (
            <li key={i} className="flex items-center justify-between gap-2">
              <span>{s.email}{s.notified && <span className="text-sage text-xs"> · notified</span>}</span>
              <button data-testid={`launch-sub-del-${i}`} onClick={() => del(s.email)}
                className="text-wine text-xs uppercase tracking-widest hover:underline">remove</button>
            </li>
          ))}
          {!subs.length && <li className="text-espresso/40">No early-access signups yet.</li>}
        </ul>
      </Card>
    </div>
  );
};

export default LaunchAdmin;
